import { html } from 'htm/preact';
import { ConcelhoRecord, NationalSummary } from '@utils/eclipse2026';
import { EclipseDisc } from './EclipseDisc';
import { HorizonCard } from './HorizonCard';
import { formatCoords, Selection } from '../lib/selection';

/**
 * A coluna da direita: o que está escolhido, seja um concelho ou um ponto qualquer
 * do mapa.
 *
 * Três regras de honestidade que vêm do DATA.md e não são negociáveis aqui:
 *
 * 1. A hora de fim é sempre `displayEnd`, nunca `partialEnd`. Em 34 concelhos
 *    do continente (todo o Algarve incluído) o Sol põe-se antes do fim astronómico;
 *    mostrar a hora em que o eclipse acaba abaixo do horizonte seria falso para quem
 *    está a olhar para o céu.
 * 2. Nunca "100%" para um concelho cujo `eclipseKind` é `partial`. Bragança são
 *    99,9% na sede — a totalidade existe na faixa do mapa, nunca no número de uma sede.
 * 3. Um clique fora de Portugal mostra **só** a banda de ocultação. Não temos horas
 *    para Espanha, e interpolá-las seria inventar precisão.
 */

function TimesRow({ concelho }: { concelho: ConcelhoRecord }) {
    return html`
        <dl class="eclipse-times">
            <div>
                <dt>Início</dt>
                <dd>${concelho.partialBegin.label}</dd>
            </div>
            <div>
                <dt>Máximo</dt>
                <dd>${concelho.peak.label}</dd>
            </div>
            <div>
                <dt>Fim</dt>
                <dd>${concelho.displayEnd.label}</dd>
            </div>
        </dl>
    `;
}

function ConcelhoView({ concelho, distritoNome }: { concelho: ConcelhoRecord; distritoNome: string }) {
    const c = concelho;
    const isTotal = c.eclipseKind === 'total'; // guarda explícita — não acontece em nenhuma sede
    const outOfFrame = c.region !== 'continente';

    return html`
        <div class="eclipse-panel__head">
            <p class="eclipse-panel__eyebrow">
                <span>${distritoNome}</span>
                <span class="eclipse-badge">${isTotal ? 'Total' : 'Parcial'}</span>
            </p>
            <h3 class="eclipse-panel__title">${c.nome}</h3>
            <p class="eclipse-panel__coords">${formatCoords(c.sede.lat, c.sede.lon)}</p>
        </div>

        <div class="eclipse-card eclipse-card--hero">
            <div class="eclipse-card__body">
                <p class="eclipse-card__title">Ocultação máxima</p>
                <p class="eclipse-hero__value">${isTotal ? '100%' : c.obscurationLabel}</p>
                <p class="eclipse-hero__meta">Máximo às <strong>${c.peak.label}</strong></p>
            </div>
            <${EclipseDisc} obscurationPct=${c.obscurationPct} />
        </div>

        <${TimesRow} concelho=${c} />

        <${HorizonCard} altitudeDeg=${c.peak.sunAltitude ?? 0} label=${`${Math.round(c.peak.sunAltitude ?? 0)}°`} />

        ${c.sunSetsBeforeEnd && c.sunset && html`<p class="eclipse-note">O Sol põe-se às ${c.sunset.label}, antes do fim astronómico do eclipse — a hora indicada acima já reflete isso.</p>`} ${c.totality?.grazing && html`<p class="eclipse-note eclipse-note--totality">${c.totality.note}</p>`}
        ${outOfFrame && html`<p class="eclipse-note">${c.region === 'madeira' ? 'A Madeira aparece' : 'Os Açores aparecem'} no mapa a oeste da Península, sem cor por cima: a ocultação aqui fica abaixo dos 90%, o valor mais baixo que as bandas desenham. O número acima é o real, calculado para a sede do concelho.</p>`}
    `;
}

function PointView({ point }: { point: Extract<Selection, { kind: 'point' }>['point'] }) {
    const place = point.countryName ?? 'Mar';

    return html`
        <div class="eclipse-panel__head">
            <p class="eclipse-panel__eyebrow"><span>${place}</span></p>
            <h3 class="eclipse-panel__title">Ponto no mapa</h3>
            <p class="eclipse-panel__coords">${formatCoords(point.lat, point.lon)}</p>
        </div>

        ${point.inTotality
            ? html`
                  <div class="eclipse-card eclipse-card--hero">
                      <div class="eclipse-card__body">
                          <p class="eclipse-card__title">Faixa de totalidade</p>
                          <p class="eclipse-hero__value">100%</p>
                          <p class="eclipse-hero__meta">Aqui o Sol fica completamente tapado.</p>
                      </div>
                      <${EclipseDisc} obscurationPct=${100} />
                  </div>
              `
            : point.thresholdLabel
              ? html`
                    <div class="eclipse-card eclipse-card--hero">
                        <div class="eclipse-card__body">
                            <p class="eclipse-card__title">Ocultação máxima</p>
                            <p class="eclipse-hero__value">≥ ${point.thresholdLabel}</p>
                            <p class="eclipse-hero__meta">Valor da banda que cobre este ponto.</p>
                        </div>
                        <${EclipseDisc} obscurationPct=${point.thresholdPct ?? 90} />
                    </div>
                `
              : html`<p class="eclipse-note">Neste ponto a ocultação fica abaixo de 90% — fora das bandas desenhadas no mapa.</p>`}

        <p class="eclipse-note">As horas de início, máximo e fim são calculadas concelho a concelho e só existem para Portugal. Clique num concelho português, ou use os menus acima, para as ver. As cidades espanholas no mapa mostram a ocultação no máximo.</p>
    `;
}

/**
 * Estado vazio: em vez de uma frase de instruções sozinha numa coluna de 800 px,
 * mostra os extremos nacionais — todos lidos de `event.national`, nenhum número
 * escrito à mão aqui (é a mesma regra do banner da homepage).
 */
function EmptyView({ national, dateLabel }: { national: NationalSummary; dateLabel: string }) {
    return html`
        <div class="eclipse-panel__head">
            <p class="eclipse-panel__eyebrow"><span>Portugal continental</span></p>
            <h3 class="eclipse-panel__title">${dateLabel}</h3>
        </div>

        <p class="eclipse-panel__prompt">Clique no mapa — ou escolha o distrito e o concelho — para ver a que horas o eclipse começa, atinge o máximo e termina na sua terra.</p>

        <div class="eclipse-card">
            <p class="eclipse-card__title">Ocultação máxima no país</p>
            <p class="eclipse-hero__value">${national.obscurationPct.minLabel} a ${national.obscurationPct.maxLabel}</p>
            <p class="eclipse-hero__meta">Máximo entre as <strong>${national.peak.firstLabel}</strong> e as <strong>${national.peak.lastLabel}</strong>, de norte para sul.</p>
        </div>

        <p class="eclipse-note eclipse-note--totality">Em Portugal, a totalidade só se vê numa faixa estreita da serra de Montesinho, no concelho de Bragança — assinalada no mapa. No resto do país o Sol nunca fica completamente tapado. A faixa de totalidade continua para norte e leste, por Espanha e outros países — visível no mapa ao afastar a vista.</p>
    `;
}

export function ResultPanel({ selection, concelho, distritoNome, national, dateLabel }: { selection: Selection; concelho: ConcelhoRecord | null; distritoNome: string; national: NationalSummary; dateLabel: string }) {
    if (selection?.kind === 'point') {
        return html`<div class="eclipse-panel">
            <${PointView} point=${selection.point} />
        </div>`;
    }

    if (concelho) {
        return html`<div class="eclipse-panel">
            <${ConcelhoView} concelho=${concelho} distritoNome=${distritoNome} />
        </div>`;
    }

    return html`<div class="eclipse-panel eclipse-panel--empty">
        <${EmptyView} national=${national} dateLabel=${dateLabel} />
    </div>`;
}
